import { useEffect, useState } from 'react';
import './App.css';
import api, { getToken, logout } from './api';
import Login from './Login';
import Visor from './Visor';

const FIRMA_VIA_LABELS = {
  manuscrita_escaneada: 'Manuscrita escaneada',
  otp_email: 'OTP por email',
  certificada: 'Certificada',
  sin_firma: 'Sin firma',
};

const ESTADO_BADGE = {
  vigente: 'nv-badge-ok',
  reemplazado: 'nv-badge-info',
  anulado: 'nv-badge-err',
};

function esSesionExpirada(err) {
  return err?.response?.status === 401;
}

function mensajeError(err, generico) {
  if (err?.response?.status === 403) {
    return 'Tu usuario no tiene permiso para ver contratos (rol insuficiente).';
  }
  return err?.response?.data?.detail || generico;
}

function formatBytes(n) {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('es-EC', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return d;
  }
}

function ListaClientes({ onSeleccionar, onSesionExpirada }) {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [intento, setIntento] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  // Debounce de la búsqueda: espera a que el usuario deje de tipear antes de disparar
  // el request, y vuelve a la página 1 (una búsqueda nueva invalida la paginación vieja).
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError('');
    const params = { page };
    if (q) params.q = q;
    api.get('/clientes', { params })
      .then((r) => { if (!cancelado) setResultado(r.data); })
      .catch((e) => {
        if (cancelado) return;
        if (esSesionExpirada(e)) { onSesionExpirada(); return; }
        setError(mensajeError(e, 'No se pudo cargar la lista de clientes.'));
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [q, page, intento, onSesionExpirada]);

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Clientes con contratos archivados</div>
          <div className="nv-page-sub">
            Vista agregada desde los documentos archivados. Un cliente sin ningún documento
            no aparece acá.
          </div>
        </div>
        <div className="nv-page-actions">
          <div className="nv-search">
            <span aria-hidden="true">🔎</span>
            <input
              placeholder="Buscar por nombre, RUC o número de contrato…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="nv-card">
        {cargando ? (
          <div className="nv-loading">
            <span className="nv-spinner" />
            Cargando clientes…
          </div>
        ) : error ? (
          <div className="nv-alert nv-alert-err" style={{ flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
            <span>{error}</span>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={() => setIntento((i) => i + 1)}>
              Reintentar
            </button>
          </div>
        ) : !resultado || resultado.data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text-muted)', fontSize: 12.5 }}>
            {q ? 'Sin resultados para esa búsqueda.' : 'Todavía no hay documentos archivados.'}
          </div>
        ) : (
          <>
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>RUC</th>
                    <th>N° de contrato</th>
                    <th>Documentos</th>
                    <th>Vigentes</th>
                    <th>Último movimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.data.map((c) => (
                    <tr
                      key={c.client_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onSeleccionar(c.client_id, c.cliente_nombre)}
                    >
                      <td>{c.cliente_nombre || '—'}</td>
                      <td className="mono">{c.cliente_ruc || '—'}</td>
                      <td className="mono">{c.numero_contrato || '—'}</td>
                      <td>{c.total_documentos}</td>
                      <td>{c.vigentes}</td>
                      <td>{formatDate(c.ultimo_movimiento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, flexWrap: 'wrap', gap: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {resultado.total} cliente{resultado.total === 1 ? '' : 's'} · página {resultado.page} de {resultado.pages}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="nv-btn nv-btn-secondary nv-btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ← Anterior
                </button>
                <button
                  className="nv-btn nv-btn-secondary nv-btn-sm"
                  disabled={page >= resultado.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DetalleCliente({ clientId, clientNombre, onVolver, onSesionExpirada }) {
  const [documentos, setDocumentos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [intento, setIntento] = useState(0);
  const [verDoc, setVerDoc] = useState(null);
  const [descargandoId, setDescargandoId] = useState(null);
  const [anularDoc, setAnularDoc] = useState(null);
  const [anulando, setAnulando] = useState(false);
  const [anularError, setAnularError] = useState('');

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError('');
    api.get(`/clientes/${clientId}/documentos`)
      .then((r) => { if (!cancelado) setDocumentos(r.data.data || []); })
      .catch((e) => {
        if (cancelado) return;
        if (esSesionExpirada(e)) { onSesionExpirada(); return; }
        setError(
          e.response?.status === 400
            ? 'Identificador de cliente inválido.'
            : mensajeError(e, 'No se pudo cargar los documentos de este cliente.')
        );
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [clientId, intento, onSesionExpirada]);

  async function descargar(doc) {
    setDescargandoId(doc.id);
    try {
      const r = await api.get(`/documentos/${doc.id}/archivo`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.archivo_nombre || 'documento.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (esSesionExpirada(e)) { onSesionExpirada(); return; }
      window.alert('No se pudo descargar el documento.');
    } finally {
      setDescargandoId(null);
    }
  }

  async function confirmarAnular() {
    if (!anularDoc) return;
    setAnulando(true);
    setAnularError('');
    try {
      await api.patch(`/documentos/${anularDoc.id}/estado`, { estado: 'anulado' });
      setDocumentos((docs) => docs.map((d) => (d.id === anularDoc.id ? { ...d, estado: 'anulado' } : d)));
      setAnularDoc(null);
    } catch (e) {
      if (esSesionExpirada(e)) { onSesionExpirada(); return; }
      setAnularError(
        e.response?.status === 403
          ? 'Tu rol no puede anular documentos.'
          : e.response?.data?.detail || 'No se pudo anular el documento.'
      );
    } finally {
      setAnulando(false);
    }
  }

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">{clientNombre || 'Cliente'}</div>
          <div className="nv-page-sub">Documentos archivados de este cliente.</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary" onClick={onVolver}>← Volver a la lista</button>
        </div>
      </div>

      <div className="nv-card">
        {cargando ? (
          <div className="nv-loading">
            <span className="nv-spinner" />
            Cargando documentos…
          </div>
        ) : error ? (
          <div className="nv-alert nv-alert-err" style={{ flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
            <span>{error}</span>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={() => setIntento((i) => i + 1)}>
              Reintentar
            </button>
          </div>
        ) : documentos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text-muted)', fontSize: 12.5 }}>
            Este cliente no tiene documentos archivados.
          </div>
        ) : (
          <div className="nv-table-wrap">
            <table className="nv-table">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Tamaño</th>
                  <th>Vía de firma</th>
                  <th>Fecha</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {documentos.map((d) => (
                  <tr key={d.id}>
                    <td>{d.archivo_nombre || '—'}</td>
                    <td>{d.tipo || '—'}</td>
                    <td>
                      <span className={`nv-badge ${ESTADO_BADGE[d.estado] || 'nv-badge-muted'}`}>
                        {d.estado || '—'}
                      </span>
                    </td>
                    <td className="mono">{formatBytes(d.bytes)}</td>
                    <td>{FIRMA_VIA_LABELS[d.firma_via] || d.firma_via || '—'}</td>
                    <td>{formatDate(d.created_at)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setVerDoc(d.id)}>
                        Ver
                      </button>{' '}
                      <button
                        className="nv-btn nv-btn-secondary nv-btn-sm"
                        disabled={descargandoId === d.id}
                        onClick={() => descargar(d)}
                      >
                        {descargandoId === d.id ? 'Descargando…' : 'Descargar'}
                      </button>{' '}
                      {d.estado === 'vigente' && (
                        <button
                          className="nv-btn nv-btn-danger nv-btn-sm"
                          onClick={() => { setAnularError(''); setAnularDoc(d); }}
                        >
                          Anular
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {verDoc && (
        <Visor documentoId={verDoc} onClose={() => setVerDoc(null)} onSesionExpirada={onSesionExpirada} />
      )}

      {anularDoc && (
        <div className="nv-modal-overlay" onClick={() => !anulando && setAnularDoc(null)}>
          <div className="nv-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="nv-modal-header">
              <span className="nv-modal-title">Anular documento</span>
              <button className="nv-modal-close" onClick={() => setAnularDoc(null)} disabled={anulando} aria-label="Cerrar">×</button>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              Vas a anular <strong>{anularDoc.archivo_nombre}</strong>. Un documento anulado
              no se puede volver a poner vigente: si hace falta, se archiva uno nuevo.
            </p>
            {anularError && <div className="nv-alert nv-alert-err" style={{ marginTop: 12 }}>{anularError}</div>}
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={() => setAnularDoc(null)} disabled={anulando}>
                Cancelar
              </button>
              <button className="nv-btn nv-btn-danger" onClick={confirmarAnular} disabled={anulando}>
                {anulando ? 'Anulando…' : 'Confirmar anulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  // Login PROPIO en este dominio: si no hay token, se muestra el formulario. Ante un 401
  // (sesión expirada/inválida) el interceptor de api.js ya limpió el token; acá solo
  // volvemos a la pantalla de login de ESTE sitio — nunca a mi.eneural.org.
  const [logueado, setLogueado] = useState(() => !!getToken());
  const [vista, setVista] = useState({ tipo: 'lista' });

  const cerrarSesion = () => { logout(); setLogueado(false); setVista({ tipo: 'lista' }); };

  if (!logueado) {
    return <Login onLogin={() => { setLogueado(true); setVista({ tipo: 'lista' }); }} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-void)' }}>
      <div className="nv-topbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="nv-topbar-title">Netvoice · Contratos</span>
        <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={cerrarSesion}>Salir</button>
      </div>
      <div className="page-content" style={{ maxWidth: 1100, margin: '0 auto' }}>
        {vista.tipo === 'lista' ? (
          <ListaClientes
            onSesionExpirada={cerrarSesion}
            onSeleccionar={(id, nombre) => setVista({ tipo: 'detalle', id, nombre })}
          />
        ) : (
          <DetalleCliente
            clientId={vista.id}
            clientNombre={vista.nombre}
            onSesionExpirada={cerrarSesion}
            onVolver={() => setVista({ tipo: 'lista' })}
          />
        )}
      </div>
    </div>
  );
}
